import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Typography } from "@material-ui/core";
import { FormattedHTMLMessage } from "react-intl";
import { debounce } from "../../util";

const InfiniteScrollAsync = ({
  emptyMessage,
  onScroll,
  params,
  render,
  take = 20,
  offsetIncrement = 50,
  loader = (
    <div style={{ textAlign: "center", marginTop: "10%" }}>
      <CircularProgress />
    </div>
  ),
  scrollContainerRef,
  shouldFilter,
}) => {
  const [loading, setLoading] = useState(false);
  const [skip, setSkip] = useState(0);
  const [items, setItems] = useState([]);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const camper = useRef(null);

  useEffect(() => {
    setItems([]);
    setSkip(0);
    setHasMoreItems(true);
  }, [params]);

  useEffect(() => {
    if (!hasMoreItems) return;

    const fetchData = async () => {
      setLoading(true);
      let newData = await onScroll({ params, skip, take });

      setHasMoreItems(newData.length === take);

      if (shouldFilter) {
        newData = shouldFilter(newData, params);
      }

      setItems((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const uniqueNewData = newData.filter(
          (item) => !existingIds.has(item.id),
        );
        return [...prev, ...uniqueNewData];
      });
      setLoading(false);
    };

    fetchData();
  }, [params, skip, onScroll, take, hasMoreItems, shouldFilter]);

  const handleScroll = useRef(
    debounce(() => {
      const container = scrollContainerRef?.current;

      const scrollTop = container ? container.scrollTop : window.pageYOffset;
      const visibleHeight = container
        ? container.clientHeight
        : window.innerHeight;

      if (
        camper.current?.offsetTop <=
          scrollTop + visibleHeight + offsetIncrement &&
        hasMoreItems &&
        !loading
      ) {
        setSkip((prev) => prev + take);
      }
    }, 200),
  ).current;

  useEffect(() => {
    const target = scrollContainerRef?.current || window;
    target.addEventListener("scroll", handleScroll);

    return () => {
      target.removeEventListener("scroll", handleScroll);
    };
  }, [handleScroll, scrollContainerRef]);

  return (
    <>
      <>
        {!items?.length && !loading && emptyMessage && (
          <Typography color="error" variant="body1" align="center">
            <FormattedHTMLMessage id={emptyMessage} />
          </Typography>
        )}
        {items.map((item) => (
          <React.Fragment key={item.id}>{render(item)}</React.Fragment>
        ))}
        {loading && loader}
        <div ref={camper} />
      </>
    </>
  );
};

export default InfiniteScrollAsync;
